/**
 * Hangman Game
 * Guess letters to reveal the word before running out of guesses
 */

import React, { useState, useEffect } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import { useKeyboard } from './hooks/useKeyboard';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Hangman = ({ onHome }) => {
  // Word list with categories
  const wordList = [
    { word: 'JAVASCRIPT', category: 'Programming' },
    { word: 'PYTHON', category: 'Programming' },
    { word: 'HANGMAN', category: 'Game' },
    { word: 'COMPUTER', category: 'Technology' },
    { word: 'ELEPHANT', category: 'Animal' },
    { word: 'MOUNTAIN', category: 'Nature' },
    { word: 'KEYBOARD', category: 'Device' },
    { word: 'ADVENTURE', category: 'Concept' },
    { word: 'BUTTERFLY', category: 'Animal' },
    { word: 'ALGORITHM', category: 'Programming' },
    { word: 'LIBRARY', category: 'Place' },
    { word: 'TELESCOPE', category: 'Device' },
    { word: 'ORCHESTRA', category: 'Music' },
    { word: 'DINOSAUR', category: 'Animal' },
    { word: 'THUNDER', category: 'Nature' },
  ];

  // Game State
  const [gameState, setGameState] = useState(GAME_CONFIG.states.PLAYING);
  const [wordData, setWordData] = useState(null);
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [score, setScore] = useLocalStorage('hangman_score', 0);
  const [highScore, setHighScore] = useLocalStorage('hangman_highScore', 0);
  const maxWrongGuesses = 6;

  // Initialize game
  useEffect(() => {
    const randomWord = wordList[Math.floor(Math.random() * wordList.length)];
    setWordData(randomWord);
    setGuessedLetters([]);
    setWrongGuesses(0);
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  }, []);

  // Keyboard controls
  useKeyboard((key) => {
    if (gameState === GAME_CONFIG.states.PLAYING) {
      const letter = key.toUpperCase();
      if (/^[A-Z]$/.test(letter) && !guessedLetters.includes(letter)) {
        handleGuess(letter);
      }
    }
  });

  const handleGuess = (letter) => {
    if (!wordData || guessedLetters.includes(letter)) return;

    const newGuessed = [...guessedLetters, letter];
    setGuessedLetters(newGuessed);

    const isCorrectGuess = wordData.word.includes(letter);
    if (!isCorrectGuess) {
      setWrongGuesses(wrongGuesses + 1);
    } else {
      // Award points for correct guess
      setScore(prev => prev + 10);
    }

    // Check win condition
    if (isWordComplete(wordData.word, newGuessed)) {
      setGameState(GAME_CONFIG.states.WIN);
      const finalScore = score + (maxWrongGuesses - wrongGuesses) * 5; // Bonus for fewer wrong guesses
      setScore(finalScore);
      if (finalScore > highScore) {
        setHighScore(finalScore);
      }
    }

    // Check lose condition
    if (!isCorrectGuess && wrongGuesses + 1 >= maxWrongGuesses) {
      setGameState(GAME_CONFIG.states.GAME_OVER);
      if (score > highScore) {
        setHighScore(score);
      }
    }
  };

  const isWordComplete = (word, guessed) => {
    return word.split('').every(letter => guessed.includes(letter));
  };

  const getHangmanDrawing = () => {
    const stages = [
      // Stage 0
      `
        -----
        |   |
        |
        |
        |
        |
        --------`,
      // Stage 1
      `
        -----
        |   |
        O   |
        |
        |
        |
        --------`,
      // Stage 2
      `
        -----
        |   |
        O   |
        |   |
        |
        |
        --------`,
      // Stage 3
      `
        -----
        |   |
        O   |
       /|   |
        |
        |
        --------`,
      // Stage 4
      `
        -----
        |   |
        O   |
       /|\\  |
        |
        |
        --------`,
      // Stage 5
      `
        -----
        |   |
        O   |
       /|\\  |
        |
       /
        --------`,
      // Stage 6 (Dead)
      `
        -----
        |   |
        O   |
       /|\\  |
        |
       / \\
        --------`,
    ];

    return stages[Math.min(wrongGuesses, stages.length - 1)];
  };

  const handleRestart = () => {
    const randomWord = wordList[Math.floor(Math.random() * wordList.length)];
    setWordData(randomWord);
    setGuessedLetters([]);
    setWrongGuesses(0);
    setScore(0);
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  if (!wordData) {
    return <div>Loading...</div>;
  }

  const displayWord = wordData.word
    .split('')
    .map(letter => (guessedLetters.includes(letter) ? letter : '_'))
    .join(' ');

  const correctGuesses = guessedLetters.filter(letter => wordData.word.includes(letter)).length;
  const remainingGuesses = maxWrongGuesses - wrongGuesses;

  return (
    <GameLayout
      title="Hangman 🎪"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={handleRestart}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {gameState === GAME_CONFIG.states.PLAYING && (
          <>
            {/* Hangman Drawing */}
            <div style={styles.hangmanContainer}>
              <pre style={styles.hangmanDrawing}>{getHangmanDrawing()}</pre>
            </div>

            {/* Stats */}
            <div style={styles.statsRow}>
              <div style={styles.statBox}>
                <div style={styles.statLabel}>Wrong Guesses</div>
                <div style={styles.statValue}>{wrongGuesses}/{maxWrongGuesses}</div>
              </div>
              <div style={styles.statBox}>
                <div style={styles.statLabel}>Correct Letters</div>
                <div style={styles.statValue}>{correctGuesses}/{wordData.word.length}</div>
              </div>
              <div style={styles.statBox}>
                <div style={styles.statLabel}>Remaining</div>
                <div style={styles.statValue} style={{ color: remainingGuesses <= 2 ? GAME_CONFIG.colors.danger : GAME_CONFIG.colors.accent }}>
                  {remainingGuesses}
                </div>
              </div>
            </div>

            {/* Category */}
            <div style={styles.categorySection}>
              <span style={styles.categoryLabel}>Category:</span>
              <span style={styles.categoryValue}>{wordData.category}</span>
            </div>

            {/* Word Display */}
            <div style={styles.wordCard}>
              <div style={styles.wordDisplay}>{displayWord}</div>
            </div>

            {/* Alphabet Buttons */}
            <div style={styles.alphabetContainer}>
              {Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map((letter) => {
                const isGuessed = guessedLetters.includes(letter);
                const isCorrect = wordData.word.includes(letter);

                let buttonStyle = { ...styles.letterButton };

                if (isGuessed) {
                  if (isCorrect) {
                    buttonStyle = { ...buttonStyle, ...styles.letterCorrect };
                  } else {
                    buttonStyle = { ...buttonStyle, ...styles.letterWrong };
                  }
                }

                return (
                  <button
                    key={letter}
                    onClick={() => handleGuess(letter)}
                    disabled={isGuessed}
                    style={buttonStyle}
                    title={`Press ${letter}`}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>

            {/* Guessed Letters Info */}
            <div style={styles.infoBox}>
              <div style={styles.infoTitle}>Guessed Letters:</div>
              <div style={styles.guessedLettersDisplay}>
                {guessedLetters.length === 0 ? (
                  <span style={{ color: GAME_CONFIG.colors.light }}>None yet</span>
                ) : (
                  guessedLetters.map(letter => (
                    <span
                      key={letter}
                      style={{
                        ...styles.guessedLetter,
                        color: wordData.word.includes(letter) ? GAME_CONFIG.colors.success : GAME_CONFIG.colors.danger,
                      }}
                    >
                      {letter}
                    </span>
                  ))
                )}
              </div>
            </div>
          </>
        )}

        {gameState === GAME_CONFIG.states.WIN && (
          <div style={styles.endScreen}>
            <div style={styles.endCard}>
              <h1 style={styles.winTitle}>🎉 You Won! 🎉</h1>
              <div style={styles.revealedWord}>
                <div style={styles.revealLabel}>The word was:</div>
                <div style={styles.revealValue}>{wordData.word}</div>
              </div>

              <ScoreDisplay
                score={score}
                stats={{
                  'Final Score': score,
                  'Wrong Guesses': `${wrongGuesses}/${maxWrongGuesses}`,
                  'Category': wordData.category,
                }}
              />

              <button onClick={handleRestart} className="btn btn-primary mt-4" style={styles.restartButton}>
                🎮 Play Again
              </button>
            </div>
          </div>
        )}

        {gameState === GAME_CONFIG.states.GAME_OVER && (
          <div style={styles.endScreen}>
            <div style={styles.endCard}>
              <h1 style={styles.loseTitle}>💀 Game Over 💀</h1>
              <div style={styles.revealedWord}>
                <div style={styles.revealLabel}>The word was:</div>
                <div style={styles.revealValue}>{wordData.word}</div>
              </div>

              <ScoreDisplay
                score={score}
                stats={{
                  'Final Score': score,
                  'Wrong Guesses': `${wrongGuesses}/${maxWrongGuesses}`,
                  'Category': wordData.category,
                }}
              />

              <div style={styles.loseMessage}>
                Better luck next time! Keep practicing!
              </div>

              <button onClick={handleRestart} className="btn btn-primary mt-4" style={styles.restartButton}>
                🔄 Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
    maxWidth: '700px',
  },
  hangmanContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1.5rem',
    minHeight: '250px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  hangmanDrawing: {
    fontFamily: 'monospace',
    color: GAME_CONFIG.colors.accent,
    fontSize: '14px',
    margin: 0,
    lineHeight: '1.2',
    whiteSpace: 'pre',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
    width: '100%',
  },
  statBox: {
    backgroundColor: `rgba(102, 126, 234, 0.15)`,
    border: `1px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    textAlign: 'center',
  },
  statLabel: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.8rem',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
  },
  statValue: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '1.5rem',
    fontWeight: 'bold',
  },
  categorySection: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    padding: '1rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    width: '100%',
  },
  categoryLabel: {
    color: GAME_CONFIG.colors.light,
    fontWeight: 'bold',
    fontSize: '0.9rem',
  },
  categoryValue: {
    color: GAME_CONFIG.colors.accent2,
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  wordCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '2rem 1.5rem',
    width: '100%',
    textAlign: 'center',
  },
  wordDisplay: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2.5rem',
    fontWeight: 'bold',
    letterSpacing: '0.5em',
    fontFamily: 'monospace',
  },
  alphabetContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '0.5rem',
    width: '100%',
  },
  letterButton: {
    padding: '0.6rem 0.3rem',
    backgroundColor: `rgba(102, 126, 234, 0.2)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.sm,
    color: GAME_CONFIG.colors.light,
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    transition: `all ${GAME_CONFIG.transitions.fast}`,
    minHeight: '40px',
  },
  letterCorrect: {
    backgroundColor: `rgba(72, 187, 120, 0.3)`,
    borderColor: GAME_CONFIG.colors.success,
    color: GAME_CONFIG.colors.success,
    boxShadow: `0 0 10px ${GAME_CONFIG.colors.success}`,
  },
  letterWrong: {
    backgroundColor: `rgba(252, 129, 129, 0.3)`,
    borderColor: GAME_CONFIG.colors.danger,
    color: GAME_CONFIG.colors.danger,
    boxShadow: `0 0 10px ${GAME_CONFIG.colors.danger}`,
    opacity: 0.6,
  },
  infoBox: {
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    border: `1px solid ${GAME_CONFIG.colors.accent2}`,
    borderRadius: GAME_CONFIG.radius.md,
    padding: '1rem',
    width: '100%',
  },
  infoTitle: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    textTransform: 'uppercase',
  },
  guessedLettersDisplay: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  guessedLetter: {
    fontSize: '0.95rem',
    fontWeight: 'bold',
    padding: '0.25rem 0.5rem',
    backgroundColor: `rgba(0, 0, 0, 0.3)`,
    borderRadius: '4px',
  },
  endScreen: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  endCard: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '2rem',
    width: '100%',
    maxWidth: '500px',
    textAlign: 'center',
  },
  winTitle: {
    color: GAME_CONFIG.colors.success,
    marginBottom: '1.5rem',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  loseTitle: {
    color: GAME_CONFIG.colors.danger,
    marginBottom: '1.5rem',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  revealedWord: {
    marginBottom: '1.5rem',
  },
  revealLabel: {
    color: GAME_CONFIG.colors.light,
    fontSize: '0.9rem',
    marginBottom: '0.5rem',
  },
  revealValue: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    fontWeight: 'bold',
    letterSpacing: '0.1em',
  },
  loseMessage: {
    color: GAME_CONFIG.colors.accent2,
    fontSize: '1rem',
    marginTop: '1rem',
  },
  restartButton: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
};

export default Hangman;
